import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Star } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

interface Testimonial {
  id: string;
  author_name: string;
  review_text: string;
  rating: number;
}

const FALLBACK_TESTIMONIALS: Testimonial[] = [
  {
    id: "fallback-1",
    author_name: "Michael R.",
    review_text: "Battle Born did an incredible job with full-body PPF on my Model Y. The attention to detail was outstanding and the finish is flawless.",
    rating: 5,
  },
  {
    id: "fallback-2",
    author_name: "Sarah K.",
    review_text: "Best ceramic coating service in Las Vegas. My car looks better than when I drove it off the lot. Highly recommend their XPEL packages.",
    rating: 5,
  },
  {
    id: "fallback-3",
    author_name: "David L.",
    review_text: "Professional team, great communication, and exceptional results. The window tint on my truck is perfect. Will be back for PPF.",
    rating: 5,
  },
];

const TestimonialSection = () => {
  const { data: testimonials, isLoading } = useQuery({
    queryKey: ["testimonials"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("testimonials")
        .select("*")
        .eq("is_published", true)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Testimonial[];
    },
  });

  const displayTestimonials: Testimonial[] = testimonials && testimonials.length > 0 ? testimonials : FALLBACK_TESTIMONIALS;

  return (
    <section className="py-20 px-4 bg-darker-bg">
      <div className="container mx-auto">
        <h2 className="text-4xl md:text-5xl font-heading font-bold text-center mb-12">
          What Our Clients Say
        </h2>
        <Carousel
          opts={{
            align: "start",
            loop: true,
          }}
          className="w-full"
        >
          <CarouselContent className="-ml-4">
            {displayTestimonials.map((testimonial) => (
              <CarouselItem key={testimonial.id} className="pl-4 md:basis-1/2 lg:basis-1/3">
                <Card className="bg-card border-border hover:border-accent transition-colors h-full">
                  <CardContent className="p-6 flex flex-col h-full">
                    <div className="flex gap-1 mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star
                          key={i}
                          className="w-5 h-5 fill-accent text-accent"
                        />
                      ))}
                    </div>
                    <p className="text-foreground mb-4 leading-relaxed flex-grow">
                      "{testimonial.review_text}"
                    </p>
                    <p className="text-muted-foreground font-semibold">
                      {testimonial.author_name}
                    </p>
                  </CardContent>
                </Card>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="hidden md:flex -left-12" />
          <CarouselNext className="hidden md:flex -right-12" />
        </Carousel>
      </div>
    </section>
  );
};

export default TestimonialSection;
